import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { HttpClient } from '@angular/common/http'
import { DialogModule } from 'primeng/dialog'
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { MultiSelectModule } from 'primeng/multiselect'
import { DatePickerModule } from 'primeng/datepicker'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { finalize, forkJoin } from 'rxjs'
import { InterventionsService } from '../../../../../interventions/services/interventions.service'
import { AffairsService } from '../../../../services/affairs.service'
import { ProductsService } from '../../../../../products/services/products.service'
import { AffairSearchResultModel } from '../../../../../../core/models/affair-search-result.model'
import { AffairModel } from '../../../../../../core/models/affair.model'
import { AffairProductModel } from '../../../../../../core/models/affair-product.model'
import { ProductModel } from '../../../../../../core/models/product.model'
import { PoolAffairCreatedEvent } from '../pool-affair-create-dialog/pool-affair-create-dialog.component'

/** Avertissement ERP pour un produit sélectionné (idAbm / idNrj manquant). */
interface ProductErpWarning {
  productName: string
  missingErps: string
}

/**
 * Entrée renvoyée par GET /affairs/{mlRef}/affair-products/schedulable :
 * la fusion abm/nrj ajoute idAbm/idNrj (ids ERP de l'affair product) au
 * niveau de l'entrée — le `product` imbriqué ne contient que
 * name/abbreviation/duration (pas d'ids).
 */
interface SchedulableAffairProductEntry extends AffairProductModel {
  idAbm?: number | null
  idNrj?: number | null
  interventionsCount?: number
}

/**
 * Ajout d'une affaire EROS existante au vivier d'optimisation (Programmator).
 * Deux étapes : recherche de l'affaire (mlRef, clients, adresse, réfs…),
 * puis sélection des produits (planifiables + catalogue) et fenêtre souhaitée.
 * Calqué sur pool-affair-create-dialog (draft manuel).
 */
@Component({
  selector: 'app-pool-affair-add-existing-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    DatePickerModule,
    TooltipModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './pool-affair-add-existing-dialog.component.html',
  styleUrl: './pool-affair-add-existing-dialog.component.scss',
})
export class PoolAffairAddExistingDialogComponent implements OnDestroy {
  private http = inject(HttpClient)
  private interventionsService = inject(InterventionsService)
  private affairsService = inject(AffairsService)
  private productsService = inject(ProductsService)

  @Input() set visible(value: boolean) {
    if (value && !this._visible) this.onOpen()
    this._visible = value
  }
  get visible(): boolean {
    return this._visible
  }
  private _visible = false

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() created = new EventEmitter<PoolAffairCreatedEvent>()

  // ── Étape recherche ──
  searchQuery = ''
  searchResults: AffairSearchResultModel[] = []
  searchLoading = false
  /** Une recherche a abouti (permet d'afficher l'état vide). */
  searchPerformed = false
  searchError: string | null = null
  private searchTimer: ReturnType<typeof setTimeout> | null = null
  private searchSeq = 0

  // ── Affaire sélectionnée ──
  selectedResult: AffairSearchResultModel | null = null
  affair: AffairModel | null = null
  detailsLoading = false
  detailsError: string | null = null

  // ── Produits planifiables de l'affaire ──
  schedulableProducts: SchedulableAffairProductEntry[] = []
  /** Ids d'affair-products planifiables cochés. */
  selectedSchedulableIds = new Set<number>()
  /** Quantités éditées des produits planifiables (id affair-product → qté). */
  schedulableQuantities = new Map<number, number>()

  // ── Catalogue produits (en complément) ──
  products: ProductModel[] = []
  productsLoading = false
  selectedProducts: ProductModel[] = []
  productQuantities = new Map<number, number>()

  // ── Coordonnées du chantier ──
  latitude: number | null = null
  longitude: number | null = null
  geocoding = false
  geoError: string | null = null
  /** Coordonnées obtenues par géocodage BAN (et non depuis l'affaire). */
  geocoded = false
  private geocodeSeq = 0

  // ── Fenêtre souhaitée (optionnel) ──
  /** N° de semaine ISO souhaitée : remplit la fenêtre lundi → dimanche. */
  wishedWeek: number | null = null
  wishedWeekHint = ''
  wishedWeekError = ''
  wishedPlanningStartDate: Date | null = null
  wishedPlanningEndDate: Date | null = null
  planningDeadline: Date | null = null

  submitting = false
  submitError: string | null = null

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  }

  private onOpen(): void {
    this.resetAll()
    if (this.products.length === 0 && !this.productsLoading) {
      this.productsLoading = true
      this.productsService
        .getProducts()
        .pipe(finalize(() => (this.productsLoading = false)))
        .subscribe({
          next: (products) => {
            this.products = (products ?? []).filter((p) => p.isActive)
          },
          error: () => {
            this.submitError = 'Impossible de charger le catalogue produits.'
          },
        })
    }
  }

  private resetAll(): void {
    this.searchQuery = ''
    this.searchResults = []
    this.searchLoading = false
    this.searchPerformed = false
    this.searchError = null
    this.searchSeq++
    this.resetSelection()
  }

  private resetSelection(): void {
    this.selectedResult = null
    this.affair = null
    this.detailsLoading = false
    this.detailsError = null
    this.schedulableProducts = []
    this.selectedSchedulableIds.clear()
    this.schedulableQuantities.clear()
    this.selectedProducts = []
    this.productQuantities.clear()
    this.latitude = null
    this.longitude = null
    this.geocoding = false
    this.geoError = null
    this.geocoded = false
    this.geocodeSeq++
    this.wishedWeek = null
    this.wishedWeekHint = ''
    this.wishedWeekError = ''
    this.wishedPlanningStartDate = null
    this.wishedPlanningEndDate = null
    this.planningDeadline = null
    this.submitting = false
    this.submitError = null
  }

  close(): void {
    this._visible = false
    this.visibleChange.emit(false)
  }

  // ── Recherche d'affaires ──

  onSearchQueryChange(query: string): void {
    this.searchQuery = query
    this.searchError = null
    if (this.searchTimer) clearTimeout(this.searchTimer)

    const q = query.trim()
    if (q.length < 2) {
      this.searchResults = []
      this.searchPerformed = false
      this.searchLoading = false
      this.searchSeq++
      return
    }
    this.searchTimer = setTimeout(() => this.fetchAffairs(q), 300)
  }

  private fetchAffairs(q: string): void {
    const seq = ++this.searchSeq
    this.searchLoading = true

    this.affairsService.searchAffairs(q).subscribe({
      next: (res) => {
        if (seq !== this.searchSeq) return
        this.searchLoading = false
        this.searchResults = res ?? []
        this.searchPerformed = true
      },
      error: () => {
        if (seq !== this.searchSeq) return
        this.searchLoading = false
        this.searchResults = []
        this.searchPerformed = true
        this.searchError = 'La recherche a échoué. Réessayez.'
      },
    })
  }

  /** "12/03/2026" depuis l'ISO createdAt (vide si absent/invalide). */
  formatCreatedAt(value: string | null): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('fr-FR')
  }

  /** Adresse complète d'un résultat : "12 rue X, 69001 Lyon". */
  formatResultAddress(result: AffairSearchResultModel): string {
    const cityPart = [result.workSitePostalCode, result.workSiteLocality]
      .filter(Boolean)
      .join(' ')
    return [result.workSiteAddress, cityPart].filter(Boolean).join(', ')
  }

  // ── Sélection d'une affaire ──

  selectResult(result: AffairSearchResultModel): void {
    this.resetSelection()
    this.selectedResult = result
    this.detailsLoading = true

    forkJoin({
      affair: this.affairsService.getAffairById(result.mlRef),
      schedulable: this.productsService.getSchedulableProductsByMlRef(
        result.mlRef
      ),
    })
      .pipe(finalize(() => (this.detailsLoading = false)))
      .subscribe({
        next: ({ affair, schedulable }) => {
          this.affair = affair
          this.schedulableProducts = schedulable ?? []
          // Pré-sélectionner tous les produits planifiables de l'affaire
          for (const ap of this.schedulableProducts) {
            this.selectedSchedulableIds.add(ap.id)
            this.schedulableQuantities.set(ap.id, Math.max(1, ap.quantity || 1))
          }
          this.resolveCoordinates(affair)
        },
        error: () => {
          this.detailsError =
            "Impossible de charger le détail de l'affaire. Réessayez."
        },
      })
  }

  /** Retour à l'étape de recherche (conserve la requête et les résultats). */
  backToSearch(): void {
    this.resetSelection()
  }

  // ── Coordonnées : affaire → sinon géocodage BAN ──

  private resolveCoordinates(affair: AffairModel): void {
    const lat = this.parseCoord(affair.workSiteLatitude)
    const lng = this.parseCoord(affair.workSiteLongitude)

    if (lat !== null && lng !== null) {
      this.latitude = lat
      this.longitude = lng
      this.geocoded = false
      return
    }

    // Pas de coordonnées sur l'affaire : géocodage BAN sur CP + ville
    // uniquement (centroïde de la commune — PAS l'adresse complète, source
    // d'erreurs de géolocalisation sur des libellés de voie approximatifs).
    const postalCode = (affair.workSitePostalCode ?? '').trim()
    const locality = (affair.workSiteLocality ?? '').trim()

    if (!locality && !postalCode) {
      this.geoError =
        "L'affaire n'a ni coordonnées ni CP/ville de chantier exploitables : impossible de la géolocaliser pour le calcul des tournées."
      return
    }

    const seq = ++this.geocodeSeq
    this.geocoding = true

    // Géoplateforme (data.geopf.fr) : successeur de api-adresse.data.gouv.fr
    // (BAN historique décommissionnée — renvoie des réponses vides).
    const url =
      'https://data.geopf.fr/geocodage/search' +
      `?q=${encodeURIComponent(locality || postalCode)}` +
      (postalCode ? `&postcode=${encodeURIComponent(postalCode)}` : '') +
      '&type=municipality&limit=1'

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (seq !== this.geocodeSeq) return
        this.geocoding = false
        const coords = res?.features?.[0]?.geometry?.coordinates
        if (Array.isArray(coords) && coords.length >= 2) {
          this.longitude = coords[0]
          this.latitude = coords[1]
          this.geocoded = true
        } else {
          this.geoError =
            "Commune du chantier introuvable (CP + ville) : impossible de géolocaliser l'affaire pour le calcul des tournées."
        }
      },
      error: () => {
        if (seq !== this.geocodeSeq) return
        this.geocoding = false
        this.geoError =
          'Le géocodage de la commune du chantier a échoué : impossible d\'ajouter l\'affaire sans coordonnées.'
      },
    })
  }

  /** parseFloat robuste ; 0/vide/invalide = coordonnée absente. */
  private parseCoord(value: string | null | undefined): number | null {
    if (value == null || String(value).trim() === '') return null
    const n = parseFloat(String(value))
    return Number.isFinite(n) && n !== 0 ? n : null
  }

  get hasCoordinates(): boolean {
    return this.latitude !== null && this.longitude !== null
  }

  // ── Produits planifiables de l'affaire ──

  toggleSchedulable(ap: AffairProductModel): void {
    if (this.selectedSchedulableIds.has(ap.id)) {
      this.selectedSchedulableIds.delete(ap.id)
    } else {
      this.selectedSchedulableIds.add(ap.id)
      if (!this.schedulableQuantities.has(ap.id)) {
        this.schedulableQuantities.set(ap.id, Math.max(1, ap.quantity || 1))
      }
    }
  }

  isSchedulableSelected(ap: AffairProductModel): boolean {
    return this.selectedSchedulableIds.has(ap.id)
  }

  getSchedulableQuantity(ap: AffairProductModel): number {
    return this.schedulableQuantities.get(ap.id) ?? Math.max(1, ap.quantity || 1)
  }

  changeSchedulableQuantity(ap: AffairProductModel, delta: number): void {
    const next = Math.max(1, this.getSchedulableQuantity(ap) + delta)
    this.schedulableQuantities.set(ap.id, next)
  }

  /** "1h30" / "45 min" depuis une durée produit en minutes. */
  formatDuration(minutes: number | null | undefined): string {
    const min = minutes ?? 0
    if (min <= 0) return '—'
    const h = Math.floor(min / 60)
    const m = min % 60
    return h > 0 ? `${h}h${m > 0 ? String(m).padStart(2, '0') : ''}` : `${m} min`
  }

  // ── Catalogue (en complément) ──

  onProductsChange(products: ProductModel[]): void {
    this.selectedProducts = products ?? []
    for (const product of this.selectedProducts) {
      if (!this.productQuantities.has(product.id)) {
        this.productQuantities.set(product.id, 1)
      }
    }
  }

  getQuantity(product: ProductModel): number {
    return this.productQuantities.get(product.id) ?? 1
  }

  changeQuantity(product: ProductModel, delta: number): void {
    const next = Math.max(1, this.getQuantity(product) + delta)
    this.productQuantities.set(product.id, next)
  }

  // ── Sélection agrégée (planifiables + catalogue) ──

  private get selectedSchedulableEntries(): SchedulableAffairProductEntry[] {
    return this.schedulableProducts.filter((ap) =>
      this.selectedSchedulableIds.has(ap.id)
    )
  }

  get selectedProductsCount(): number {
    return this.selectedSchedulableEntries.length + this.selectedProducts.length
  }

  /** Durée de service totale estimée, en minutes. */
  get totalDurationMin(): number {
    const schedulable = this.selectedSchedulableEntries.reduce(
      (sum, ap) =>
        sum + (ap.product?.duration || 0) * this.getSchedulableQuantity(ap),
      0
    )
    const catalogue = this.selectedProducts.reduce(
      (sum, p) => sum + (p.duration || 0) * this.getQuantity(p),
      0
    )
    return schedulable + catalogue
  }

  get totalDurationLabel(): string {
    const min = this.totalDurationMin
    if (min <= 0) return ''
    const h = Math.floor(min / 60)
    const m = min % 60
    return h > 0 ? `${h}h${m > 0 ? String(m).padStart(2, '0') : ''}` : `${m} min`
  }

  /**
   * Produits sélectionnés absents d'un ERP : la planification sur un
   * technicien de cet ERP pourra être refusée par le back.
   * - Catalogue : présence = idAbm / idNrj du produit (mapping catalogue).
   * - Planifiables : présence = idAbm / idNrj de l'ENTRÉE (ids ERP de
   *   l'affair product, ajoutés par la fusion abm/nrj) — le `product`
   *   imbriqué ne porte pas ces ids.
   */
  get erpWarnings(): ProductErpWarning[] {
    const warnings: ProductErpWarning[] = []
    const seenNames = new Set<string>()

    const check = (
      name: string,
      idAbm: number | null | undefined,
      idNrj: number | null | undefined
    ) => {
      if (!name || seenNames.has(name)) return
      seenNames.add(name)
      const missing: string[] = []
      if (idAbm == null) missing.push('ABM')
      if (idNrj == null) missing.push('NRJ')
      if (missing.length > 0) {
        warnings.push({
          productName: name,
          missingErps: missing.join(' / '),
        })
      }
    }

    for (const ap of this.selectedSchedulableEntries) {
      check(
        ap.product?.abbreviation || ap.product?.name || '',
        ap.idAbm,
        ap.idNrj
      )
    }
    for (const p of this.selectedProducts) {
      check(p.abbreviation || p.name, p.idAbm, p.idNrj)
    }

    return warnings
  }

  // ── Semaine souhaitée (même pattern que le dialog manuel) ──

  onWishedWeekChange(value: number | string | null): void {
    this.wishedWeekError = ''
    const week = Number(value)

    if (!value || isNaN(week)) {
      this.wishedWeek = null
      this.wishedWeekHint = ''
      return
    }

    if (week < 1 || week > 53 || !Number.isInteger(week)) {
      this.wishedWeek = week
      this.wishedWeekHint = ''
      this.wishedWeekError = 'N° de semaine invalide (1 à 53).'
      return
    }

    this.wishedWeek = week

    const now = new Date()
    let year = now.getFullYear()
    let [monday, sunday] = this.isoWeekRange(week, year)

    // Semaine entièrement passée → année suivante
    if (sunday.getTime() < now.setHours(0, 0, 0, 0)) {
      year += 1
      ;[monday, sunday] = this.isoWeekRange(week, year)
    }

    this.wishedPlanningStartDate = monday
    this.wishedPlanningEndDate = sunday

    const fmt = (d: Date) =>
      d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    this.wishedWeekHint = `S${week} ${year} : du ${fmt(monday)} au ${fmt(sunday)}`
  }

  /** Modification manuelle des dates : la semaine saisie ne fait plus foi. */
  onWishedDatesEdited(): void {
    this.wishedWeek = null
    this.wishedWeekHint = ''
    this.wishedWeekError = ''
  }

  /** [lundi, dimanche] de la semaine ISO `week` de l'année `year`. */
  private isoWeekRange(week: number, year: number): [Date, Date] {
    // Le 4 janvier est toujours en semaine ISO 1
    const jan4 = new Date(year, 0, 4)
    const jan4IsoDay = jan4.getDay() === 0 ? 7 : jan4.getDay()
    const week1Monday = new Date(year, 0, 4 - (jan4IsoDay - 1))

    const monday = new Date(week1Monday)
    monday.setDate(week1Monday.getDate() + (week - 1) * 7)
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    return [monday, sunday]
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  // ── Soumission ──

  get canSubmit(): boolean {
    return (
      !this.submitting &&
      !this.detailsLoading &&
      !this.geocoding &&
      this.selectedResult !== null &&
      this.affair !== null &&
      this.selectedProductsCount > 0 &&
      this.hasCoordinates
    )
  }

  submit(): void {
    const result = this.selectedResult
    if (!this.canSubmit || !result) return

    this.submitting = true
    this.submitError = null

    // Planifiables : identifiés par l'id du PRODUIT côté ERP (erpProductId +
    // erpSource) — la table locale affair_product ne contient que les produits
    // en sous-traitance, idMylink est donc souvent absent. name/abbreviation/
    // duration servent de repli si le produit n'a pas d'équivalent catalogue.
    const affairProducts = [
      ...this.selectedSchedulableEntries.map((ap) => ({
        erpProductId: ap.product?.id ?? null,
        erpSource: ap.erpSource ?? null,
        affairProductId: ap.idMylink ?? null,
        idAbm: ap.idAbm ?? null,
        idNrj: ap.idNrj ?? null,
        name: ap.product?.name ?? null,
        abbreviation: ap.product?.abbreviation ?? null,
        duration: ap.product?.duration ?? null,
        quantity: this.getSchedulableQuantity(ap),
      })),
      ...this.selectedProducts.map((p) => ({
        productId: p.id,
        quantity: this.getQuantity(p),
      })),
    ]

    this.interventionsService
      .addExistingAffairToPool({
        mlRef: result.mlRef,
        affairProducts,
        latitude: this.latitude,
        longitude: this.longitude,
        wishedPlanningStartDate: this.wishedPlanningStartDate
          ? this.toIsoDate(this.wishedPlanningStartDate)
          : null,
        wishedPlanningEndDate: this.wishedPlanningEndDate
          ? this.toIsoDate(this.wishedPlanningEndDate)
          : null,
        planningDeadline: this.planningDeadline
          ? this.toIsoDate(this.planningDeadline)
          : null,
      })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (response) => {
          this.created.emit({
            ...response,
            customerLastname: result.finalCustomers || result.mlRef,
          })
          this.close()
        },
        error: (err) => {
          this.submitError =
            err?.error?.message ??
            "Une erreur est survenue lors de l'ajout de l'affaire au vivier."
        },
      })
  }
}
